// src/components/widget-wrappers/MobileDrawer.tsx
import { Box, Flex } from '@chakra-ui/react'
import { Drawer, Portal } from '@chakra-ui/react'
import { useMemo, type ReactNode } from 'react'
import { useDrawerSwipeToClose } from '@/hooks/useDrawerSwipeToClose'

type DrawerVariant = 'full' | 'half'

type MobileDrawerProps = {
  open: boolean
  onClose: () => void
  header?: ReactNode
  children: ReactNode
  footer?: ReactNode

  /** Enables swipe down to close via the handle (default: true) */
  swipeToClose?: boolean

  /**
   * Optional tuning for swipe feel. If not provided, reasonable defaults are used.
   * NOTE: these are gesture UX values, not drawer sizing.
   */
  swipeThreshold?: number
  swipeRubber?: number
  variant?: DrawerVariant
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full'
  placement?: 'start' | 'end' | 'top' | 'bottom'
}

export function MobileDrawer({
  open,
  onClose,
  header,
  children,
  footer,
  swipeToClose = true,
  variant = 'full',
  swipeThreshold,
  swipeRubber,
  size,
  placement,
}: MobileDrawerProps) {
  const swipeConfig = useMemo(() => {
    if (variant === 'half') {
      return {
        threshold: 55,
        rubber: 100,
      }
    }

    return {
      threshold: 100,
      rubber: 170,
    }
  }, [variant])

  const swipe = useDrawerSwipeToClose({
    open,
    onClose,
    enabled: swipeToClose,
    threshold: swipeThreshold ?? swipeConfig.threshold,
    rubber: swipeRubber ?? swipeConfig.rubber,
  })

  const computedSize = useMemo(() => {
    if (size) return size
    return variant === 'half' ? 'lg' : 'full'
  }, [size, variant])

  const computedPlacement = useMemo(() => placement ?? 'bottom', [placement])

  return (
    <Drawer.Root
      open={open}
      onOpenChange={e => !e.open && onClose()}
      placement={computedPlacement}
      size={computedSize}
    >
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content style={swipe.style as any}>
            {/* Drag handle (only this area listens to swipe) */}
            {swipeToClose && (
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                height="48px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                cursor="grab"
                {...swipe.handlers}
                style={{ touchAction: 'none' }}
              >
                <Box width="46px" height="4px" borderRadius="full" bg="gray.300" />
              </Box>
            )}

            <Flex direction="column" height="100%" flex="1" minH={0}>
              {header ? <Box mb={4}>{header}</Box> : null}

              {/* Body */}
              <Box
                flex={1}
                h="full"
                overflowY="auto"
                css={{
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}
              >
                {children}
              </Box>

              {/* Footer */}
              {footer ? <Box pt={2}>{footer}</Box> : null}
            </Flex>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}
